import {
  Translate,
  type Translation,
  useTranslation,
} from '@shared/translation';
import type { RowData } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { getColumnDefaults } from './column';
import type { TableColumnDef } from './tanstackTypes';
import type { ExtendedColumnDef } from './types';

function getColumnName<TTexts>(
  columnName: ((texts: TTexts) => Translation) | undefined,
  headerText: ((texts: TTexts) => Translation) | undefined,
  texts: TTexts
): Translation {
  if (columnName) {
    return columnName(texts);
  }
  if (headerText) {
    return headerText(texts);
  }
  return '';
}

export const useExtendedColumnDefs = <TData extends RowData, TTexts = never>(
  columnDefs: ExtendedColumnDef<TData, TTexts>[]
): TableColumnDef<TData>[] => {
  const { texts, translate } = useTranslation<TTexts>();

  const createColumnDef = useCallback(
    <TData extends RowData>({
      headerText,
      columnName,
      columns = [],
      ...other
    }: ExtendedColumnDef<TData, TTexts>): TableColumnDef<TData> => {
      const columnDefaults = getColumnDefaults(other.meta?.dataType);
      const columnNameTranslation = getColumnName(
        columnName,
        headerText,
        texts
      );
      const unit = other.meta?.unit;

      const filterFn = other.filterFn ?? columnDefaults?.filterFn;

      return {
        ...columnDefaults,
        ...other,
        ...(filterFn !== undefined && { filterFn }),
        header:
          other.header ??
          (() => (
            <>
              <Translate text={headerText?.(texts) ?? ''} />
              {unit && <>&nbsp;({unit})</>}
            </>
          )),
        meta: {
          ...columnDefaults?.meta,
          ...other.meta,
          name: translate(columnNameTranslation),
        },
        columns: columns.map(createColumnDef),
      } as TableColumnDef<TData>;
    },
    [texts, translate]
  );

  return useMemo(
    () => columnDefs.map(createColumnDef),
    [columnDefs, createColumnDef]
  );
};
